// The balance as David wants it read (2026-10-16): three bars that sum to 100% of
// Expected. Collected, AC (allowed less collected: patient balances, denials, in
// flight) and OA (expected less allowed: underpaid vs the fee schedule), each as a
// share of Expected, and, when the server's identity does not close to the cent, a
// fourth bar labelled "unexplained" rather than a hidden remainder. Contractual
// (charges less expected) sits below the baseline, outside the stack, as a line of
// text. Inline SVG, no chart library.

import React from "react";

const W = 760;
const H = 150;
const PX = 44;
const PT = 26;
const PB = 40;

export const BALANCE_FILL = {
  collected: "#1f7a4d",
  ac: "#b26a00",
  oa: "#2d4f8a",
  unexplained: "#b3261e",
};

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// The bars, exported so the test can check the shares: each is its dollars over
// Expected; the unexplained bar appears only when the server reports a remainder.
export function balanceBars(balance) {
  if (!balance) return [];
  const expected = num(balance.expected);
  const share = (v) => (expected > 0 ? (num(v) / expected) * 100 : 0);
  const bars = [
    {
      key: "collected",
      label: "Collected",
      dollars: num(balance.collected),
      pct: share(balance.collected),
    },
    {
      key: "ac",
      label: "AC: allowed − collected",
      dollars: num(balance.ac),
      pct: share(balance.ac),
    },
    {
      key: "oa",
      label: "OA: expected − allowed",
      dollars: num(balance.oa),
      pct: share(balance.oa),
    },
  ];
  if (Math.abs(num(balance.unexplained)) >= 0.005) {
    bars.push({
      key: "unexplained",
      label: "unexplained",
      dollars: num(balance.unexplained),
      pct: share(balance.unexplained),
    });
  }
  return bars;
}

const money = (v) =>
  `$${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BalanceChart({ balance }) {
  const bars = balanceBars(balance);
  if (!bars.length) return null;
  const n = bars.length;
  const bw = (W - PX - 16) / n;
  const y = (v) =>
    PT + (H - PT - PB) * (1 - Math.min(Math.max(v, 0), 100) / 100);
  return (
    <div className="wf-chart wf-balance" data-testid="waterfall-balance-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Collected, AC and OA as shares of Expected"
      >
        {[0, 50, 100].map((v) => (
          <g key={`g${v}`}>
            <line x1={PX} x2={W - 8} y1={y(v)} y2={y(v)} className="wf-grid" />
            <text
              x={PX - 6}
              y={y(v) + 4}
              fontSize="10"
              textAnchor="end"
              className="wf-muted"
            >
              {v}%
            </text>
          </g>
        ))}
        {bars.map((b, i) => {
          const x = PX + i * bw + bw * 0.12;
          const w = bw * 0.76;
          const top = y(b.pct);
          const h = Math.max(y(0) - top, 2);
          return (
            <g key={b.key}>
              <rect
                className="wf-bar"
                data-balance={b.key}
                x={x}
                y={top}
                width={w}
                height={h}
                rx="2"
                fill={BALANCE_FILL[b.key]}
              />
              <text
                x={x + w / 2}
                y={top - 5}
                fontSize="11"
                fontWeight="600"
                textAnchor="middle"
                className="wf-fg"
              >
                {`${b.pct.toFixed(1)}% · ${money(b.dollars)}`}
              </text>
              <text
                x={x + w / 2}
                y={H - PB + 14}
                fontSize="10"
                textAnchor="middle"
                className="wf-muted"
              >
                {b.label}
              </text>
            </g>
          );
        })}
        <text
          x={PX}
          y={H - 6}
          fontSize="10"
          className="wf-muted"
          data-testid="waterfall-balance-footer"
        >
          {`Expected ${money(balance.expected)} = 100%`}
        </text>
      </svg>
    </div>
  );
}
